import {
  type BaseQueryFn,
  type FetchArgs,
  fetchBaseQuery,
  type FetchBaseQueryError,
} from "@reduxjs/toolkit/query";
import toast from "react-hot-toast";

import { ENV_VARS } from "../constants/env";
import { ENUM_AUTH_TOKENS, LOGOUT_USER_ACTION } from "@/utils/enums/auth.enums";
import {
  callServerLogout,
  clearAuthTokens,
  getAuthTokensFromLocalStorage,
  silentRefresh,
  type RefreshFailureReason,
} from "@/utils/auth";
import { goToConnectionLost } from "@/utils/connection-lost";
import { recordServerTimestamp } from "@/utils/serverClock";
import { LANGUAGE_HEADER, requestLanguage } from "@/i18n/request-language";
import i18n from "@/i18n";

const secondApiRoutes: string[] = [];

const cleanParams = (
  params: Record<string, unknown>,
): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(params).filter(
      ([, v]) => v !== null && v !== undefined && v !== "",
    ),
  );

const prepareHeaders = (headers: Headers): Headers => {
  headers.set(LANGUAGE_HEADER, requestLanguage());
  const authTokens = getAuthTokensFromLocalStorage();
  const existingAuthorization = headers.get("Authorization");
  if (!existingAuthorization && authTokens) {
    headers.set(
      "Authorization",
      `Bearer ${authTokens[ENUM_AUTH_TOKENS.ACCESS_TOKEN]}`,
    );
  }
  return headers;
};

// Uses the shared silentRefresh mutex from auth.ts — coordinates with
// useValidateAuth, useProactiveTokenRefresh and socket recovery so only ONE
// refresh request ever flies at a time across all callers.
const attemptTokenRefresh = async (): Promise<{
  accessToken: string | null;
  reason?: RefreshFailureReason;
}> => {
  const result = await silentRefresh();
  if (!result.ok) return { accessToken: null, reason: result.reason };
  const stored = getAuthTokensFromLocalStorage();
  return { accessToken: stored?.[ENUM_AUTH_TOKENS.ACCESS_TOKEN] ?? null };
};

export const dynamicBaseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const requestUrl = typeof args === "string" ? args : args.url;
  const isSecondApi = secondApiRoutes.some((route) => requestUrl.includes(route));

  const baseUrl = isSecondApi ? ENV_VARS.API_INSTANCE2 : ENV_VARS.API_INSTANCE;

  const rawBaseQuery = fetchBaseQuery({
    baseUrl,
    prepareHeaders,
  });

  if (
    typeof args === "object" &&
    args.params &&
    typeof args.params === "object"
  ) {
    args = { ...args, params: cleanParams(args.params) };
  }

  const t0 = Date.now();
  const result = await rawBaseQuery(args, api, extraOptions);
  const body = (result.data ?? result.error?.data) as
    | { timestamp?: unknown }
    | null
    | undefined;
  recordServerTimestamp(body?.timestamp, t0, Date.now());
  const status = result?.meta?.response?.status;
  const currentPath =
    typeof window !== "undefined" ? window.location.pathname : "";

  if (status === 401 && !currentPath.startsWith("/auth")) {
    const { accessToken: newAccessToken, reason } = await attemptTokenRefresh();

    if (newAccessToken) {
      return rawBaseQuery(args, api, extraOptions);
    }

    // The refresh never reached the BE (down/restarting) — the session may well
    // still be valid, so keep the tokens and park the user instead.
    if (reason === "network") {
      goToConnectionLost();
      return result;
    }

    await callServerLogout();
    toast.error(i18n.t("common:session.expired_sign_in_again"));
    clearAuthTokens();
    api.dispatch({ type: LOGOUT_USER_ACTION.NAME });
  }

  return result;
};
